import type { SetStateAction } from 'react';
import React from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { IRate } from '@src/@types/microcredits';
import { MobileFilterControl } from '@src/components/details/components/FilterControl/components/MobileFilterControl';
import { AmountControlV2 } from '@src/components/ui/AmountControlV2';
import { TermControl } from '@src/components/ui/TermControl';
import { FILTERS_NAMES } from '@src/constants/filters';
import { getAmountString, humanizeTerm } from '@src/helpers/common';
import { getDaysPluralize } from '@src/helpers/strings';
import { useCardData } from '@src/hooks/useCardData';
import { AMOUNT_LABEL, AMOUNT_MAX, AMOUNT_SLIDER_STEP, TERM_LABEL } from 'features/FullDeal/config';
import { CurrencySymbol } from 'shared/config/internalization';
import { HelperText } from 'shared/ui/HelperText';

import styles from './styles.module.scss';

type Props = {
  initialAmount: number;
  initialTerm: number;
  rubRates: Array<Omit<IRate, '__v' | 'product' | 'isProgressive'>>;
  offerId: string;
  setTerm: React.Dispatch<SetStateAction<number>>;
  setAmount: React.Dispatch<SetStateAction<number>>;
  eventLabel: string;
};

// @todo: Копмпонент очень похож на ProductListFilterSortPanel, мб можно их как переиспользовать?
export const FilterControl = ({
  initialAmount,
  initialTerm,
  rubRates,
  offerId,
  setTerm,
  setAmount,
  eventLabel,
}: Props) => {
  const isMobile = useIsMobile();
  const { amountMin, amountMax, term } = useCardData(rubRates);

  const termPostfix = getDaysPluralize(initialTerm);
  const amountHelperText = `от ${getAmountString(amountMin)} до ${getAmountString(amountMax)} ${CurrencySymbol.RUB}`;

  const termMin = humanizeTerm(term.from, term.fromUnit, { termWithUnit: false });
  const termMax = humanizeTerm(term.to, term.toUnit, { termWithUnit: false });

  const termsHelperText = `от ${termMin} до ${termMax} ${getDaysPluralize(Number(termMax))}`;

  if (isMobile) {
    return (
      <MobileFilterControl
        setTerm={setTerm}
        setAmount={setAmount}
        initialAmount={initialAmount}
        initialTerm={initialTerm}
        amountHelperText={amountHelperText}
        termsHelperText={termsHelperText}
        eventLabel={eventLabel}
        offerId={offerId}
        amountMin={amountMin}
        amountMax={amountMax}
        term={term}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <div className={styles.sumField}>
          <AmountControlV2
            name={FILTERS_NAMES.AMOUNT}
            label={AMOUNT_LABEL}
            value={initialAmount}
            min={amountMin}
            max={amountMax}
            postfix={CurrencySymbol.RUB}
            onChange={setAmount}
            steps={[{ ceilThreshold: AMOUNT_MAX, step: AMOUNT_SLIDER_STEP }]}
            offerId={offerId}
          />
          <HelperText text={amountHelperText} />
        </div>

        <div className={styles.sumField}>
          <div>
            <TermControl
              name={FILTERS_NAMES.TERM}
              label={TERM_LABEL}
              value={initialTerm}
              onChange={setTerm}
              postfix={termPostfix}
              min={Number(termMin)}
              max={Number(termMax)}
              offerId={offerId}
            />
            <HelperText text={termsHelperText} />
          </div>
        </div>
      </div>
    </div>
  );
};
